import React, { useCallback, useState } from 'react'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import { Icon } from '~/components/atoms/Icon/Icon'
import RangeSlider from '~/components/atoms/RangeSlider/RangeSlider'
import Text, { TextSizes } from '~/components/atoms/Text/Text'

import styles from './LocationFilter.module.css'

const continents = [
  'UK',
  'Europe',
  'USA',
  'Africa',
  'Asia',
  'Australasia',
  'UAE',
]

interface LocationFilterProps {
  locationChangeHandler: (
    location: string,
    isPostcode?: boolean,
    range?: number
  ) => void
  selectedLocation: string | null
}

const LocationFilter: React.FC<LocationFilterProps> = ({
  locationChangeHandler,
  selectedLocation,
}) => {
  const t = useTranslations('common')
  const [searchValue, setSearchValue] = useState('')

  const [range, setRange] = useState(0)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [postcodeList, setPostcodeList] = useState<string[]>([])

  const clearSearch = () => {
    setSearchValue('')
    setPostcodeList([])
    setRange(0)
  }

  const searchPostcodes = useCallback(async (searchTerm: string) => {
    if (searchTerm === '') {
      setSearchValue('')
      setPostcodeList([])
      setRange(0)
      return
    }

    try {
      const response = await fetch(
        `https://api.postcodes.io/postcodes?autocomplete=true&q=${searchTerm}`
      )
      const data = await response.json()
      const postcodes = data.result.map(
        (item: { postcode: string }) => item.postcode
      )
      setPostcodeList(postcodes)
    } catch (error) {
      setPostcodeList([])
      console.error('Error:', error)
    }
  }, [])

  const handleLocationChange = (
    location: string,
    isPostcode?: boolean,
    range?: number
  ) => {
    locationChangeHandler(location, isPostcode, !isPostcode ? undefined : range)
  }

  return (
    <>
      <div className={styles.base}>
        {/* Input with search icon */}
        <div className={styles.searchFieldContainer}>
          <div
            className={classNames(
              styles.searchField,
              isSearchOpen && styles.searchOpen
            )}
          >
            <i className={styles.searchIcon}>
              <Icon type="Magnify" width={15} height={15} />
            </i>
            <input
              type="text"
              name="q"
              className={classNames(styles.textInput)}
              value={searchValue}
              autoComplete="off"
              placeholder={t('startTypingPostcode')}
              onFocus={() => setIsSearchOpen(true)}
              onInput={e => {
                setSearchValue(e.currentTarget.value)
                searchPostcodes(e.currentTarget.value)
              }}
            />

            {searchValue && (
              <div
                role="button"
                className={classNames(styles.closeButton, {
                  [styles['canClose']]: isSearchOpen,
                })}
                onClick={() => {
                  clearSearch()
                }}
              >
                <span className={styles.cross}>x</span>
              </div>
            )}
          </div>
        </div>
        {searchValue && (
          <div className={styles.rangeContainer}>
            <Text
              uppercase
              size={TextSizes.bodySml}
              className={styles.rangeTitle}
            >
              Within
            </Text>
            <RangeSlider
              value={range}
              smallHeight
              useDualSlider={false}
              step={10}
              handleChange={function (e: any): void {
                setRange(e)
              }}
            />
            <Text size={TextSizes.bodySml} className={styles.rangeRange}>
              {range === 0 ? 'This location only' : `${range} miles`}
            </Text>
          </div>
        )}
        <div className={styles.continentList}>
          {postcodeList && postcodeList.length === 0 && searchValue && (
            <div className={styles.rangeContainer}>
              <Text size={TextSizes.bodySml} className={styles.rangeRange}>
                No postcodes found matching search term
              </Text>
            </div>
          )}
          {postcodeList &&
            postcodeList.length > 0 &&
            postcodeList.map((postcode, idx) => (
              <div
                className={styles.continent}
                key={`postcode-${idx}`}
                onClick={() => handleLocationChange(postcode, true, range)}
              >
                <Text size={TextSizes.bodyLrg} uppercase>
                  {postcode}
                </Text>
              </div>
            ))}
          {!searchValue && (
            <>
              {continents.map((continent, idx) => (
                <div
                  className={styles.continent}
                  key={`continent-${idx}`}
                  onClick={() => handleLocationChange(continent, false, range)}
                >
                  <Text size={TextSizes.bodyLrg} uppercase>
                    {continent}
                  </Text>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default LocationFilter
